import React from "react";
import { Document, Page, Text, View, Link } from "@react-pdf/renderer";
import {
  type RawSection,
  type CVSettings,
  DEFAULT_CV_SETTINGS,
} from "./cv.types";
import {
  extractCandidateInfo,
  extractAboutData,
  extractExperienceData,
  extractPublicationsData,
  extractResearchData,
  extractTeachingData,
  extractProjectsData,
  extractSkillsData,
} from "./cv-data-extractor";
import { createCVStyles } from "./cv-styles";

export interface AcademicCVProps {
  profile: any;
  sections: RawSection[];
  settings?: Partial<CVSettings>;
}

export const AcademicCV: React.FC<AcademicCVProps> = ({
  profile,
  sections = [],
  settings: customSettings,
}) => {
  const settings: CVSettings = {
    ...DEFAULT_CV_SETTINGS,
    ...customSettings,
  };

  const styles = createCVStyles({
    template: settings.template,
    fontFamily: settings.fontFamily,
    accentColor: settings.accentColor,
    density: settings.density,
  });

  const candidate = extractCandidateInfo(profile, sections);

  // Filter and order sections according to settings
  const activeSections = React.useMemo(() => {
    const visibleOnly = sections.filter((s) => s.visible);
    if (!settings.includedSectionIds || settings.includedSectionIds.length === 0) {
      return visibleOnly;
    }
    // Filter to only included section IDs and preserve the user's custom ordering
    const idMap = new Map(sections.map((s) => [s.id, s]));
    const ordered: RawSection[] = [];
    for (const id of settings.includedSectionIds) {
      const sec = idMap.get(id);
      if (sec && sec.visible) {
        ordered.push(sec);
      }
    }
    // Append any newly added sections not yet in includedSectionIds
    for (const sec of visibleOnly) {
      if (!settings.includedSectionIds.includes(sec.id)) {
        ordered.push(sec);
      }
    }
    return ordered;
  }, [sections, settings.includedSectionIds]);

  // Helper to render contact line / items
  const renderContactItems = (isSidebar = false) => {
    const items: Array<{ key: string; label: string; url?: string }> = [];

    if (settings.showEmail && candidate.contact.email) {
      items.push({ key: "email", label: candidate.contact.email, url: `mailto:${candidate.contact.email}` });
    }
    if (settings.showPhone && candidate.contact.phone) {
      items.push({ key: "phone", label: candidate.contact.phone, url: `tel:${candidate.contact.phone}` });
    }
    if (settings.showLocation && candidate.contact.location) {
      items.push({ key: "location", label: candidate.contact.location });
    }
    if (settings.showWebsite && candidate.contact.websiteUrl) {
      items.push({ key: "website", label: "Website", url: candidate.contact.websiteUrl });
    }
    if (settings.showScholar && candidate.contact.googleScholarUrl) {
      items.push({ key: "scholar", label: "Google Scholar", url: candidate.contact.googleScholarUrl });
    }
    if (settings.showOrcid && candidate.contact.orcidUrl) {
      items.push({ key: "orcid", label: "ORCID", url: candidate.contact.orcidUrl });
    }
    if (settings.showLinkedin && candidate.contact.linkedinUrl) {
      items.push({ key: "linkedin", label: "LinkedIn", url: candidate.contact.linkedinUrl });
    }
    if (settings.showGithub && candidate.contact.githubUrl) {
      items.push({ key: "github", label: "GitHub", url: candidate.contact.githubUrl });
    }

    if (items.length === 0) return null;

    return (
      <View style={styles.contactRow}>
        {items.map((item, index) => (
          <View key={item.key} style={styles.contactItem}>
            {!isSidebar && index > 0 && <Text style={styles.contactSeparator}>•</Text>}
            {item.url ? (
              <Link src={item.url} style={styles.link}>
                {item.label}
              </Link>
            ) : (
              <Text>{item.label}</Text>
            )}
          </View>
        ))}
      </View>
    );
  };

  // Section Renderers
  const renderAbout = (section: RawSection) => {
    const data = extractAboutData(section.content, candidate.bio);
    if (!data.body && data.highlights.length === 0) return null;

    const title = settings.customSectionTitles[section.id] || data.heading;

    return (
      <View key={section.id} style={styles.section} wrap={false}>
        <View style={styles.sectionHeaderContainer}>
          <Text style={styles.sectionTitle}>{title}</Text>
        </View>
        {data.body ? <Text style={styles.paragraph}>{data.body}</Text> : null}
      </View>
    );
  };

  const renderExperience = (section: RawSection) => {
    const data = extractExperienceData(section.content);
    if (data.items.length === 0) return null;

    const title = settings.customSectionTitles[section.id] || data.heading;

    return (
      <View key={section.id} style={styles.section}>
        <View style={styles.sectionHeaderContainer}>
          <Text style={styles.sectionTitle}>{title}</Text>
        </View>
        {data.items.map((item: any) => (
          <View key={item.id} style={styles.itemGroup} wrap={false}>
            <View style={styles.itemHeaderRow}>
              <View style={{ flex: 1, paddingRight: 8 }}>
                <Text style={styles.itemTitle}>{item.role}</Text>
              </View>
              <Text style={styles.itemDate}>
                {item.startDate}{item.startDate && item.endDate ? " – " : ""}{item.endDate}
              </Text>
            </View>
            <View style={styles.itemSubtitleRow}>
              <View style={{ flex: 1, paddingRight: 8 }}>
                <Text style={styles.itemSubtitle}>
                  {item.organization}
                  {item.department ? `, ${item.department}` : ""}
                </Text>
              </View>
              {item.location && <Text style={styles.itemLocation}>{item.location}</Text>}
            </View>
            {item.description && <Text style={styles.paragraph}>{item.description}</Text>}
          </View>
        ))}
      </View>
    );
  };

  const renderPublications = (section: RawSection) => {
    const data = extractPublicationsData(section.content);
    if (data.items.length === 0) return null;

    const title = settings.customSectionTitles[section.id] || data.heading;

    return (
      <View key={section.id} style={styles.section}>
        <View style={styles.sectionHeaderContainer}>
          <Text style={styles.sectionTitle}>{title}</Text>
        </View>
        {data.items.map((pub: any, idx: number) => (
          <View key={pub.id} style={(styles as any).pubItem} wrap={false}>
            <View style={(styles as any).pubHeaderRow}>
              {settings.citationStyle === "numbered" && (
                <Text style={(styles as any).pubNumber}>[{idx + 1}]</Text>
              )}
              <View style={(styles as any).pubContent}>
                <Text style={styles.citationText}>
                  {settings.showPublicationBadges && pub.type ? (
                    <Text style={styles.badge}>{pub.type.toUpperCase()} </Text>
                  ) : null}
                  {pub.authors ? `${pub.authors}. ` : ""}
                  "{pub.title}"{pub.year ? ` (${pub.year})` : ""}.
                  {pub.venue ? ` In: ${pub.venue}.` : ""}
                </Text>
                <View style={{ flexDirection: "row", gap: 8, marginTop: 2 }}>
                  {pub.doi && (
                    <Link
                      src={pub.doi.startsWith("http") ? pub.doi : `https://doi.org/${pub.doi}`}
                      style={styles.link}
                    >
                      DOI: {pub.doi}
                    </Link>
                  )}
                  {pub.url && !pub.doi && (
                    <Link src={pub.url} style={styles.link}>
                      Link to Publication
                    </Link>
                  )}
                  {pub.citationCount ? (
                    <Text style={{ fontSize: 8, color: "#64748b" }}>
                      Citations: {pub.citationCount}
                    </Text>
                  ) : null}
                </View>
                {settings.showAbstracts && pub.abstract && (
                  <Text style={[styles.paragraph, { marginTop: 3, fontStyle: "italic" }]}>
                    {pub.abstract}
                  </Text>
                )}
              </View>
            </View>
          </View>
        ))}
      </View>
    );
  };

  const renderResearch = (section: RawSection) => {
    const data = extractResearchData(section.content);
    if (!data.summary && data.areas.length === 0) return null;

    const title = settings.customSectionTitles[section.id] || data.heading;

    return (
      <View key={section.id} style={styles.section} wrap={false}>
        <View style={styles.sectionHeaderContainer}>
          <Text style={styles.sectionTitle}>{title}</Text>
        </View>
        {data.summary && <Text style={[styles.paragraph, { marginBottom: 6 }]}>{data.summary}</Text>}
        {data.areas.map((area: any) => (
          <View key={area.id} style={(styles as any).researchAreaItem} wrap={false}>
            <Text style={(styles as any).researchAreaTitle}>• {area.title}</Text>
            {area.description && (
              <Text style={(styles as any).researchAreaDescription}>{area.description}</Text>
            )}
            {area.keywords.length > 0 && (
              <Text style={(styles as any).researchAreaKeywords}>
                Keywords: {area.keywords.join(", ")}
              </Text>
            )}
          </View>
        ))}
      </View>
    );
  };

  const renderTeaching = (section: RawSection) => {
    const data = extractTeachingData(section.content);
    if (data.items.length === 0) return null;

    const title = settings.customSectionTitles[section.id] || data.heading;

    return (
      <View key={section.id} style={styles.section}>
        <View style={styles.sectionHeaderContainer}>
          <Text style={styles.sectionTitle}>{title}</Text>
        </View>
        {data.items.map((course: any) => (
          <View key={course.id} style={styles.itemGroup} wrap={false}>
            <View style={styles.itemHeaderRow}>
              <View style={{ flex: 1, paddingRight: 8 }}>
                <Text style={styles.itemTitle}>
                  {settings.showCourseCodes && course.courseCode ? `[${course.courseCode}] ` : ""}
                  {course.courseTitle}
                </Text>
              </View>
              {course.term && <Text style={styles.itemDate}>{course.term}</Text>}
            </View>
            <View style={styles.itemSubtitleRow}>
              <View style={{ flex: 1, paddingRight: 8 }}>
                <Text style={styles.itemSubtitle}>
                  {course.role.replace("_", " ").toUpperCase()} • {course.level.toUpperCase()}
                  {course.institution ? ` (${course.institution})` : ""}
                </Text>
              </View>
              {course.courseUrl && (
                <Link src={course.courseUrl} style={styles.link}>
                  Syllabus
                </Link>
              )}
            </View>
            {course.description && <Text style={styles.paragraph}>{course.description}</Text>}
          </View>
        ))}
      </View>
    );
  };

  const renderProjects = (section: RawSection) => {
    const data = extractProjectsData(section.content);
    if (data.items.length === 0) return null;

    const title = settings.customSectionTitles[section.id] || data.heading;

    return (
      <View key={section.id} style={styles.section}>
        <View style={styles.sectionHeaderContainer}>
          <Text style={styles.sectionTitle}>{title}</Text>
        </View>
        {data.items.map((project: any) => (
          <View key={project.id} style={styles.itemGroup} wrap={false}>
            <View style={styles.itemHeaderRow}>
              <View style={{ flex: 1, paddingRight: 8 }}>
                <Text style={styles.itemTitle}>{project.title}</Text>
              </View>
              <Text style={styles.itemDate}>
                {project.startDate}{project.startDate && project.endDate ? " – " : ""}{project.endDate}
              </Text>
            </View>
            <View style={styles.itemSubtitleRow}>
              <View style={{ flex: 1, paddingRight: 8 }}>
                <Text style={styles.itemSubtitle}>
                  Role: {project.role.replace("_", " ").toUpperCase()}
                  {project.fundingAgency ? ` • ${project.fundingAgency}` : ""}
                  {project.grantNumber ? ` (#${project.grantNumber})` : ""}
                </Text>
              </View>
              {settings.showGrantsAmount && project.amount && (
                <Text style={{ fontSize: 8.5, fontFamily: "Helvetica-Bold", color: "#166534" }}>
                  {project.amount}
                </Text>
              )}
            </View>
            {project.collaborators && (
              <Text style={{ fontSize: 8, color: "#64748b", marginBottom: 2 }}>
                Collaborators: {project.collaborators}
              </Text>
            )}
            {project.description && <Text style={styles.paragraph}>{project.description}</Text>}
            {project.projectUrl && (
              <Link src={project.projectUrl} style={[styles.link, { marginTop: 2 }]}>
                Project Details
              </Link>
            )}
          </View>
        ))}
      </View>
    );
  };

  const renderSkills = (section: RawSection) => {
    const data = extractSkillsData(section.content);
    if (data.groups.length === 0) return null;

    const title = settings.customSectionTitles[section.id] || data.heading;

    return (
      <View key={section.id} style={styles.section} wrap={false}>
        <View style={styles.sectionHeaderContainer}>
          <Text style={styles.sectionTitle}>{title}</Text>
        </View>
        {data.groups.map((grp: any) => (
          <View key={grp.id} style={{ marginBottom: 3 }}>
            <Text style={{ fontSize: 9, lineHeight: 1.4 }}>
              <Text style={{ fontFamily: "Helvetica-Bold" }}>{grp.categoryName}: </Text>
              {grp.skills.map((s: any) => s.name).join(", ")}
            </Text>
          </View>
        ))}
      </View>
    );
  };

  const renderSectionSwitch = (section: RawSection) => {
    switch (section.type) {
      case "about":
        return renderAbout(section);
      case "experience":
        return renderExperience(section);
      case "publications":
        return renderPublications(section);
      case "research":
        return renderResearch(section);
      case "teaching":
        return renderTeaching(section);
      case "projects":
        return renderProjects(section);
      case "skills":
        return renderSkills(section);
      default:
        return null;
    }
  };

  // Two-column layout handler
  if (settings.template === "compact-two-column") {
    const sidebarTypes = new Set(["about", "research", "skills"]);
    const sidebarSections = activeSections.filter((s) => sidebarTypes.has(s.type));
    const mainSections = activeSections.filter((s) => !sidebarTypes.has(s.type));

    return (
      <Document>
        <Page size={settings.pageSize} style={styles.page}>
          {/* Header */}
          <View style={styles.headerContainer}>
            {settings.showCvHeaderBanner && (
              <View style={{ marginBottom: 3, width: "100%" }}>
                <Text style={{ fontSize: 8, letterSpacing: 2, fontFamily: "Helvetica-Bold", color: "#64748b", textTransform: "uppercase" }}>
                  Curriculum Vitae
                </Text>
              </View>
            )}
            <View style={(styles as any).nameWrapper}>
              <Text style={styles.name}>{candidate.fullName}</Text>
            </View>
            {candidate.title && (
              <View style={(styles as any).titleWrapper}>
                <Text style={styles.title}>{candidate.title}</Text>
              </View>
            )}
          </View>

          {/* Two Columns */}
          <View style={(styles as any).columnsWrapper}>
            {/* Sidebar Column */}
            <View style={(styles as any).sidebarColumn}>
              <View style={styles.section}>
                <View style={styles.sectionHeaderContainer}>
                  <Text style={styles.sectionTitle}>Contact</Text>
                </View>
                {renderContactItems(true)}
              </View>
              {sidebarSections.map(renderSectionSwitch)}
            </View>

            {/* Main Column */}
            <View style={(styles as any).mainColumn}>
              {mainSections.map(renderSectionSwitch)}
            </View>
          </View>

          {/* Dynamic Running Footer */}
          <Text
            render={({ pageNumber, totalPages }) =>
              `${candidate.fullName} • Page ${pageNumber} of ${totalPages}`
            }
            fixed
            style={styles.footer}
          />
        </Page>
      </Document>
    );
  }

  // Full-width stacked layouts (academic-classic, modern-executive, minimal-clean)
  return (
    <Document>
      <Page size={settings.pageSize} style={styles.page}>
        {/* Header */}
        <View style={styles.headerContainer}>
          {settings.showCvHeaderBanner && (
            <View
              style={{
                marginBottom: 3,
                width: "100%",
                alignItems: settings.template === "academic-classic" ? "center" : "flex-start",
              }}
            >
              <Text
                style={{
                  fontSize: 8,
                  letterSpacing: 2,
                  fontFamily: "Helvetica-Bold",
                  color: "#64748b",
                  textTransform: "uppercase",
                }}
              >
                Curriculum Vitae
              </Text>
            </View>
          )}
          <View style={(styles as any).nameWrapper}>
            <Text style={styles.name}>{candidate.fullName}</Text>
          </View>
          {candidate.title ? (
            <View style={(styles as any).titleWrapper}>
              <Text style={styles.title}>{candidate.title}</Text>
            </View>
          ) : null}
          {renderContactItems(false)}
        </View>

        {/* Dynamic Sections */}
        {activeSections.map(renderSectionSwitch)}

        {/* Dynamic Running Footer */}
        <Text
          render={({ pageNumber, totalPages }) =>
            `${candidate.fullName} • Page ${pageNumber} of ${totalPages}`
          }
          fixed
          style={styles.footer}
        />
      </Page>
    </Document>
  );
};
